// src/components/InvestmentModal.jsx
import React, { useEffect, useMemo, useState } from "react";

import { instrumentMetaByValue, normalizeInstrumentType } from "../constants/investments";
import api from "../services/api";

const PAYMENT_LABELS = {
  bullet: "Bullet payment (principal and interest at maturity)",
  interest_annual: "Annual interest payouts",
  interest_semi_annual: "Semi-annual interest payouts",
  interest_quarterly: "Quarterly interest payouts",
  interest_monthly: "Monthly interest payouts",
  amortized_monthly: "Monthly blended principal & interest",
};

const currencySymbol = (code) => {
  if (!code) return "NGN";
  if (code.toUpperCase() === "USD") return "USD";
  if (code.toUpperCase() === "EUR") return "EUR";
  if (code.toUpperCase() === "GBP") return "GBP";
  return code.toUpperCase();
};

const formatCurrency = (amount, code) => {
  const value = Number(amount || 0);
  if (!Number.isFinite(value)) return `${currencySymbol(code)} 0`;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: code?.toUpperCase() || "NGN",
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
};

const maturityFromTerm = (term) => {
  if (!term) return null;
  const start = new Date();
  if (term.tenor_unit === "days") {
    return new Date(start.getTime() + term.tenor_value * 24 * 60 * 60 * 1000);
  }
  const next = new Date(start);
  if (term.tenor_unit === "years") {
    next.setFullYear(next.getFullYear() + Number(term.tenor_value || 0));
  } else {
    next.setMonth(next.getMonth() + Number(term.tenor_value || 0) * (term.tenor_unit === "months" ? 1 : 12));
  }
  return next;
};

const InvestmentModal = ({ onClose, onSuccess, presetId }) => {
  const [loading, setLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedTermCode, setSelectedTermCode] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [simulation, setSimulation] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.getInvestmentOpportunities();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        setOpportunities(list);
        const initial =
          (presetId && list.find((item) => item.id === presetId)) ||
          list[0] ||
          null;
        if (initial) {
          setSelectedId(initial.id);
          const firstTerm = (initial.terms || [])[0] || null;
          setSelectedTermCode(firstTerm?.code || "");
        }
      } catch (error) {
        console.error("Failed to load opportunities", error);
        alert("Could not load investment opportunities.");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [presetId]);

  const selected = useMemo(
    () => opportunities.find((item) => item.id === selectedId) || null,
    [opportunities, selectedId]
  );

  const terms = selected?.terms || [];
  const selectedTerm = useMemo(
    () => terms.find((term) => term.code === selectedTermCode) || terms[0] || null,
    [terms, selectedTermCode]
  );

  const minimum = useMemo(() => {
    const base = Number(selected?.min_amount || 0);
    const override = Number(selectedTerm?.min_amount || 0);
    return Math.max(base, override);
  }, [selected?.min_amount, selectedTerm?.min_amount]);

  const annualRate = Number(selectedTerm?.rate_pct || selected?.annual_rate_pct || 0);
  const currency = selected?.currency || "NGN";
  const additionalInvestmentsAllowed =
    selectedTerm?.additional_investments ?? selected?.allow_additional_investment ?? false;
  const instrumentMeta = useMemo(() => {
    if (!selected) return null;
    const rawType = String(selected.instrument_category || selected.type || "").trim();
    const normalized = normalizeInstrumentType(rawType);
    return instrumentMetaByValue[rawType] || instrumentMetaByValue[normalized] || null;
  }, [selected]);
  const instrumentLabel =
    instrumentMeta?.label || selected?.instrument_category || selected?.type || "Fixed Income";
  const instrumentDescription = instrumentMeta?.description;

  useEffect(() => {
    if (!selected || !selectedTerm) {
      setSimulation(null);
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setSimulation(null);
      return;
    }
    let cancelled = false;
    setSimLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.simulateOpportunity(selected.id, {
          amount: numericAmount,
          term_code: selectedTerm.code,
        });
        if (!cancelled) {
          setSimulation(result);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Simulation error", error);
          setSimulation(null);
        }
      } finally {
        if (!cancelled) setSimLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selected?.id, selectedTerm?.code, amount]);

  const maturityDate = useMemo(() => maturityFromTerm(selectedTerm), [selectedTerm]);
  const projectedInterest = useMemo(() => {
    if (simulation?.total_interest != null) return Number(simulation.total_interest);
    const principal = Number(amount || 0);
    if (!principal || !annualRate || !selectedTerm) return 0;
    const unit = selectedTerm.tenor_unit;
    const value = Number(selectedTerm.tenor_value || 0);
    if (!value) return 0;
    let years = 0;
    if (unit === "years") years = value;
    else if (unit === "months") years = value / 12;
    else if (unit === "days") years = value / 365;
    return principal * (annualRate / 100) * years;
  }, [simulation?.total_interest, amount, annualRate, selectedTerm]);

  const canSubmit =
    Boolean(selected && selectedTerm && Number(amount || 0) > 0 && Number(amount || 0) >= minimum) && !loading;

  const handleConfirm = async () => {
    if (!selected || !selectedTerm) {
      alert("Select an investment opportunity and term.");
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      alert("Enter a valid amount greater than zero.");
      return;
    }
    if (minimum && numericAmount < minimum) {
      alert(`Minimum investment amount is ${formatCurrency(minimum, currency)}.`);
      return;
    }

    setLoading(true);
    try {
      await api.purchaseInvestment({
        opportunity_id: selected.id,
        amount: numericAmount,
        term_code: selectedTerm.code,
        note,
      });
      alert("Investment order submitted successfully.");
      if (typeof onSuccess === "function") onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      alert(error?.message || "Failed to place investment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6">
      <div className="flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl md:max-h-[calc(100vh-3rem)]">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Invest in {selected?.name || "an opportunity"}</h2>
            <p className="text-sm text-slate-500">
              Select a structured fixed-income plan, review the projected schedule, and confirm your investment.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
            aria-label="Close"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid flex-1 gap-6 overflow-y-auto p-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Investment opportunity</label>
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                value={selectedId}
                onChange={(event) => {
                  const id = event.target.value;
                  setSelectedId(id);
                  const found = opportunities.find((item) => item.id === id);
                  const firstTerm = (found?.terms || [])[0] || null;
                  setSelectedTermCode(firstTerm?.code || "");
                }}
              >
                {opportunities.map((item) => {
                  const rawType = String(item.instrument_category || item.type || "").trim();
                  const normalized = normalizeInstrumentType(rawType);
                  const meta = instrumentMetaByValue[rawType] || instrumentMetaByValue[normalized] || null;
                  const label = meta?.label || rawType || "Instrument";
                  return (
                    <option key={item.id} value={item.id}>
                      {item.name} | {label} | Min {formatCurrency(item.min_amount, item.currency)}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Plan / payment structure</label>
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                value={selectedTerm?.code || ""}
                onChange={(event) => setSelectedTermCode(event.target.value)}
              >
                {terms.map((term) => (
                  <option key={term.code} value={term.code}>
                    {term.label}
                  </option>
                ))}
              </select>
              {selectedTerm ? (
                <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <span className="block font-semibold text-slate-700">Payment scheme</span>
                  {PAYMENT_LABELS[selectedTerm.payment_scheme] || selectedTerm.payment_scheme}
                  <span className="mt-2 block font-semibold text-slate-700">Tenor</span>
                  {selectedTerm.tenor_value} {selectedTerm.tenor_unit}
                  <span className="mt-2 block font-semibold text-slate-700">Compounding</span>
                  {selectedTerm.compounding ? "Interest is capitalised until maturity." : "Interest is paid as scheduled."}
                </p>
              ) : null}
            </div>

            {selected ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex-1">
                    <span className="text-[11px] font-semibold uppercase text-slate-500">Instrument</span>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                        {instrumentLabel}
                      </span>
                      <span>
                        Min {formatCurrency(selected.min_amount, selected.currency)}{" "}
                        {" \u00b7 "} Base rate {selected.annual_rate_pct || annualRate}%
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[11px] uppercase text-slate-500">Top-ups</span>
                    <div className={`font-semibold ${additionalInvestmentsAllowed ? "text-emerald-700" : "text-slate-600"}`}>
                      {additionalInvestmentsAllowed ? "Allowed" : "Disabled"}
                    </div>
                  </div>
                </div>
                {instrumentDescription ? (
                  <p className="mt-2 text-xs text-slate-500">{instrumentDescription}</p>
                ) : null}
                {selected?.description ? (
                  <p className="mt-2 text-xs text-slate-600">{selected.description}</p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-slate-700">Amount</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
                  {currencySymbol(currency)}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  placeholder="0.00"
                />
              </div>
              {minimum ? (
                <p className="mt-1 text-xs text-slate-500">
                  Minimum amount: {formatCurrency(minimum, currency)}
                </p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                placeholder="e.g. Diversification entry"
              />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <div className="flex justify-between">
              <span>Annual rate</span>
              <span className="font-semibold text-slate-900">{annualRate}%</span>
            </div>
            <div className="flex justify-between">
              <span>Projected interest</span>
              <span className="font-semibold text-slate-900">{formatCurrency(projectedInterest, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span>Maturity date</span>
              <span className="font-semibold text-slate-900">
                {maturityDate ? maturityDate.toLocaleDateString() : "Select a term"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Additional investments</span>
              <span className="font-semibold text-slate-900">
                {additionalInvestmentsAllowed ? "Allowed" : "Disabled"}
              </span>
            </div>
            <div className="border-t border-slate-200 pt-3 text-xs text-slate-500">
              {simLoading ? (
                <span className="text-slate-600">Calculating projected schedule...</span>
              ) : simulation ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span>Total cashflows</span>
                    <span className="font-semibold text-slate-900">
                      {simulation.cashflows?.length || 0} events
                    </span>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-100 text-slate-500">
                        <tr>
                          <th className="px-2 py-1 text-left">Due</th>
                          <th className="px-2 py-1 text-left">Type</th>
                          <th className="px-2 py-1 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(simulation.cashflows || []).slice(0, 4).map((cf) => {
                          const due = cf.due_date ? new Date(cf.due_date).toLocaleDateString() : "-";
                          const label =
                            cf.label || (cf.type ? cf.type.replace(/_/g, " ") : "interest");
                          const amount =
                            cf.display_amount != null ? cf.display_amount : cf.amount || 0;
                          return (
                            <tr key={cf.sequence}>
                              <td className="px-2 py-1">{due}</td>
                              <td className="px-2 py-1 capitalize">{label}</td>
                              <td className="px-2 py-1 text-right">
                                {formatCurrency(amount, currency)}
                              </td>
                            </tr>
                          );
                        })}
                        {(simulation.cashflows || []).length > 4 ? (
                          <tr>
                            <td colSpan={3} className="px-2 py-1 text-center text-slate-400">
                              + {(simulation.cashflows || []).length - 4} additional payments
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                "Enter an amount to preview the cashflow schedule."
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:bg-emerald-400"
          >
            {loading ? "Processing..." : "Confirm investment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentModal;


